import React from 'react';
import { Row, Col, Select } from 'antd';

import TitleRow from './TitleRow';

import OnTraccrTextInput from '../../../common/inputs/OnTraccrTextInput';

import CalculationDropdown from './CalculationDropdown';
import OptionalRow from './OptionalRow';

import FieldTriggerCheckbox from './FieldTriggerCheckbox';
import FieldTriggerFlag from './FieldTriggerFlag';
import ConditionalRenderingRow from './ConditionalRenderingRow';
import OnTraccrCheckbox from '../../../common/inputs/OnTraccrCheckbox';
import HoverHelp from '../../../common/HoverHelp';
import FieldWidthSelector from './FieldWidthSelector';
import HideIfEmptyInPDFCheckbox from './HideIfEmptyInPDFCheckbox';
import { formatCurrencyCalculation } from '../../ResponderHelpers';

const configure = ({
  id,
  sectionId,
  setConfigProps,
  configProps = {},
  setCanSubmit,
  sections = [],
  isExternalForm,
  templateId,
  divisions,
  projectId,
  name,
  isBoardCards,
  setFieldTriggerEditable,
  disableOptional,
  customers = [],
  users = [],
  costcodes = [],
  phases = [],
  projectIdMap = {},
  vendors = [],
  equipment = [],
  projects = [],
  formTemplates = [],
  labels = [],
  contactAddressBooks = {},
  buckets = [],
  scheduleCategories = [],
  isV3Form = false,
} = {}) => {
  const {
    optional,
    hasConditionalRendering = false,
    conditionalRenderingFormula,
    tags: configTags,
    costcodeFilter,
  } = configProps;

  const setTitle = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      title: value,
    });
    setCanSubmit(value);
  };

  const setOptional = (checked) => {
    setConfigProps({
      ...configProps,
      optional: checked,
    });
  };

  const onTagsAndFormulaChange = ({ tags, formula }) => {
    setConfigProps({
      ...configProps,
      tags,
      formula,
    });
  };

  const onCurrencyChange = (checked) => {
    setConfigProps({
      ...configProps,
      isCurrency: checked,
    });
  };

  const onRoundChange = (checked) => {
    setConfigProps({
      ...configProps,
      roundCalculation: checked,
    });
  };

  const onRoundingSettingChange = (roundingSetting) => {
    setConfigProps({
      ...configProps,
      roundingSetting,
    });
  };

  const setHasConditionalRendering = (checked) => {
    setConfigProps({
      ...configProps,
      hasConditionalRendering: checked,
    });
  };
  const setConditionalRenderingFormula = (formula) => {
    setConfigProps({
      ...configProps,
      conditionalRenderingFormula: formula,
    });
  };

  const setFieldTrigger = (checked) => {
    setConfigProps({
      ...configProps,
      fieldTrigger: checked,
    });
  };

  const setWidth = (value) => {
    setConfigProps({
      ...configProps,
      width: value,
    });
  };

  const onFilterChange = (e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setConfigProps({
      ...configProps,
      costcodeFilter: value,
    });
  };

  const canFilter = configTags?.some((tag) => tag.supportsFilter);

  return (
    <div>
      <Row className='form-required-field'>
        Title:
      </Row>
      <Row style={{ marginTop: 5 }}>
        <OnTraccrTextInput
          textarea placeholder='Insert title here'
          onChange={setTitle}
          value={configProps.title}
        />
      </Row>
      <OptionalRow onChange={setOptional} optional={optional} disabled={disableOptional}/>
      <ConditionalRenderingRow
        onChange={setHasConditionalRendering}
        onFormulaChange={setConditionalRenderingFormula}
        hasConditionalRendering={hasConditionalRendering}
        conditionalRenderingFormula={conditionalRenderingFormula}
        sections={sections}
        id={id}
        customers={customers}
        projects={projects}
        users={users}
        costcodes={costcodes}
        phases={phases}
        projectIdMap={projectIdMap}
        vendors={vendors}
        equipment={equipment}
        formTemplates={formTemplates}
        labels={labels}
        contactAddressBooks={contactAddressBooks}
        buckets={buckets}
        scheduleCategories={scheduleCategories}
      />
      <HideIfEmptyInPDFCheckbox
        isV3Form={isV3Form}
        configProps={configProps}
        setConfigProps={setConfigProps}
      />
      <OnTraccrCheckbox
        style={{ marginTop: 10 }}
        onChange={onCurrencyChange}
        value={configProps.isCurrency}
        label='Is Currency?'
        hoverNode={(
          <div style={{ width:250}}>
            Check this box if you want the result to have a '$' prefix (e.g. $25.00)
          </div>
        )}
      />
      <Row style={{ marginTop: 10 }} gutter={10} align="middle">
        <Col>
          <OnTraccrCheckbox
            onChange={onRoundChange}
            value={configProps.roundCalculation}
            label="Round to Whole Number"
          />
        </Col>
        <Col>
          <Select
            style={{ width: 130 }}
            value={configProps.roundingSetting ?? 'roundUp'}
            onChange={onRoundingSettingChange}
            disabled={!configProps.roundCalculation}
            options={[
              { value: 'roundUp', label: 'Round Up' },
              { value: 'roundDown', label: 'Round Down' },
            ]}
          />
        </Col>
      </Row>
      <Row className='form-required-field' style={{ marginTop: 15 }}>
        Formula:
      </Row>
      <CalculationDropdown
        id={id}
        sectionId={sectionId}
        sections={sections}
        onTagsAndFormulaChange={onTagsAndFormulaChange}
        formula={configProps.formula}
        tags={configProps.tags}
      />
      {
        canFilter && (
        <Row style={{ marginTop: 5 }} gutter={4} align="middle">
          <Col>
            Filter by Cost Code
          </Col>
          <Col>
            <OnTraccrTextInput onChange={onFilterChange} defaultValue={costcodeFilter} />
          </Col>
          <Col>
            <HoverHelp
              placement="bottom"
              content={(
                <div style={{ maxWidth: 300 }}>
                  Enter a cost code or name.
                  <br /> <br />
                  the calculation will only use values
                  that <strong>exactly</strong> match the code or name 
                </div>
              )}
            />
          </Col>
        </Row>
      )}
      <FieldWidthSelector
        value={configProps.width}
        onChange={setWidth}
      />
      {!isBoardCards && !isExternalForm && (
        <FieldTriggerCheckbox
          onChange={setFieldTrigger}
          onEditableChange={setFieldTriggerEditable}
          sections={sections}
          projectId={projectId}
          templateId={templateId}
          divisions={divisions}
          configProps={configProps}
          setConfigProps={setConfigProps}
          name={name}
        />
      )}
    </div>
  );
};

const preview = ({
  id,
  configProps = {},
  responses = {}, // Actively responding
  previewProps = {}, // Completed form
  responding = false,
  sections,
  projectId,
  templateId,
  divisions,
  isDisplay,
  onFieldTrigger,
  isFormBuilder,
} = {}) => {
  const isFieldDisplay = !isFormBuilder && isDisplay;

  const {
    optional,
    title = 'Title goes here',
    isCurrency,
    fieldTrigger,
    roundCalculation,
  } = configProps;

  const {
    value: previewValue = roundCalculation && !isCurrency ? '0' : '0.00',
  } = previewProps;

  const {
    [id]: {
      value = (isCurrency ? '0.00' : '0'),
    } = {},
  } = responses;
  let displayValue = `${responding ? value : previewValue}`;
  if (isCurrency) {
    if (isDisplay) displayValue = displayValue.replace(/\$\s?/g, '');
    displayValue = formatCurrencyCalculation(displayValue);
  }

  return (
    <div>
      <TitleRow
        title={title}
        optional={optional}
        isFieldDisplay={isFieldDisplay}
        filter={
          fieldTrigger && !isDisplay
            ? (
              <FieldTriggerFlag
                sections={sections}
                templateId={templateId}
                projectId={projectId}
                divisions={divisions}
                configProps={configProps}
                responding={responding}
                onFieldTrigger={onFieldTrigger}
              />
            ) : null
        }
      />
      <Row style={{ marginTop: 15, minHeight: 32 }}>
        <div className="form-field-display-value-text">{displayValue}</div>
      </Row>
    </div>
  );
};

export default {
  configure,
  preview,
  title: 'Calculation',
  description: 'Use this field to calculate values from numbers or other field values',
};
